import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { segnaTappa } from "@/components/VisitaOspite";
import api from "@/lib/api";
import axios from "axios";
import { API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { photoUrl, DEFAULT_ITEM_PHOTO } from "@/lib/placeholders";
import { Truck, AlertTriangle, Wrench, Calendar, ShieldCheck, Clock, CheckCircle2 } from "lucide-react";

// Pagina aperta scansionando il QR applicato sulla macchina: nessun login.
// Mostra di che mezzo si tratta e se è in regola. Storico interventi,
// fornitori, costi, note e file dei documenti restano nel gestionale.
const DOC_LABELS = { ce: "Scheda CE", maintenance: "Manutenzione",
                     logbook: "Libro manutenzione", taratura: "Taratura",
                     libretto: "Libretto di circolazione", revisione: "Revisione",
                     altro: "Altro documento" };
const STATUS_META = {
  valido: { label: "Valido", icon: CheckCircle2, box: "border-emerald-200 bg-emerald-50", badge: "bg-emerald-600 text-white" },
  in_scadenza: { label: "In scadenza", icon: Clock, box: "border-amber-200 bg-amber-50", badge: "bg-amber-500 text-white" },
  scaduto: { label: "Scaduto", icon: AlertTriangle, box: "border-red-200 bg-red-50", badge: "bg-red-600 text-white" },
  senza_scadenza: { label: "Senza scadenza", icon: CheckCircle2, box: "border-slate-200 bg-slate-50", badge: "bg-slate-500 text-white" },
};
const STATO_MEZZO = { active: "Operativa", maintenance: "In manutenzione", dismissed: "Dismessa" };

const fmtData = (iso) => iso ? iso.substring(0, 10).split("-").reverse().join("/") : null;

export default function AttrezzaturaPubblica() {
  const { id } = useParams();

  // Se a leggere questo QR e' un ospite in visita, la tappa entra nel suo
  // giro. Per tutti gli altri non succede niente.
  useEffect(() => { segnaTappa("attrezzatura", id); }, [id]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/equipment/${id}`).then(r => setData(r.data))
      .catch((e) => {
        if (e.response?.status === 401 || e.response?.status === 403) {
          // La targa non e' piu' una pagina aperta: chi non ha un account ne'
          // una visita in corso passa dal codice, e poi torna qui.
          window.location.replace(
            `/visita?vai=${encodeURIComponent(window.location.pathname)}`);
          return;
        }
        setError("Attrezzatura non trovata");
      });
  }, [id]);

  if (error) return <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
    <Card><CardContent className="p-8 text-center">
      <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
      <h2 className="font-display text-2xl font-bold">{error}</h2>
    </CardContent></Card>
  </div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Caricamento...</div>;

  const { equipment: eq, documents } = data;
  const scaduti = documents.filter(d => d.status === "scaduto");
  const manutenzioneScaduta = eq.maintenance_status === "scaduto";
  const regolare = scaduti.length === 0 && !manutenzioneScaduta && eq.status !== "dismissed";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-2 bg-warning-stripe" />
        <div className="max-w-3xl mx-auto px-6 py-8 relative">
          <div className="flex items-start gap-4 flex-wrap">
            <img src={photoUrl(eq.photo_url) || DEFAULT_ITEM_PHOTO} alt=""
                 className="w-20 h-20 rounded object-cover bg-white/10 border-2 border-white/20 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.3em] text-slate-400">Scheda attrezzatura</div>
              <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter break-all">{eq.identifier}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-300">
                <Badge className="bg-orange-500 text-white border-0">{eq.category}</Badge>
                <span>{[eq.brand, eq.model, eq.year].filter(Boolean).join(" · ")}</span>
              </div>
            </div>
            <div className="text-xs text-slate-400 text-right">{data.company}</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-5">
        <Card className={regolare ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}>
          <CardContent className="p-5 flex items-center gap-3">
            {regolare
              ? <ShieldCheck className="w-8 h-8 text-emerald-600 flex-shrink-0" />
              : <AlertTriangle className="w-8 h-8 text-red-600 flex-shrink-0" />}
            <div>
              <div className={`font-display text-lg font-bold ${regolare ? "text-emerald-800" : "text-red-800"}`}>
                {regolare ? "Mezzo in regola" : "Verifiche non in regola"}
              </div>
              <div className="text-sm text-slate-600">
                {regolare
                  ? "Documenti validi e manutenzione nei termini."
                  : [manutenzioneScaduta ? "manutenzione scaduta" : null,
                     scaduti.length ? `${scaduti.length} document${scaduti.length === 1 ? "o scaduto" : "i scaduti"}` : null]
                      .filter(Boolean).join(" · ")}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2">
              <Truck className="w-5 h-5 text-orange-500" /> Stato del mezzo
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div><span className="text-slate-500">Condizione:</span> {STATO_MEZZO[eq.status] || eq.status}</div>
              {eq.tipo && <div><span className="text-slate-500">Tipo:</span> {eq.tipo}</div>}
              {eq.next_maintenance_date && (
                <div className="sm:col-span-2 flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-500">Prossima manutenzione:</span>
                  <span className={manutenzioneScaduta ? "text-red-600 font-semibold" : "text-emerald-700 font-semibold"}>
                    {fmtData(eq.next_maintenance_date)}{manutenzioneScaduta && " · scaduta"}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-orange-500" /> Documenti e certificazioni
            </h2>
            {documents.length === 0 ? (
              <p className="text-sm text-slate-500">Nessun documento registrato per questo mezzo.</p>
            ) : (
              <div className="space-y-2">
                {documents.map(d => {
                  const meta = STATUS_META[d.status] || STATUS_META.senza_scadenza;
                  const Icon = meta.icon;
                  return (
                    <div key={d.id} className={`p-3 rounded border ${meta.box} flex items-center gap-3`}>
                      <Icon className="w-4 h-4 flex-shrink-0 text-slate-600" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{d.title}</div>
                        <div className="text-xs text-slate-500">
                          {DOC_LABELS[d.doc_type] || d.doc_type}
                          {d.expires_date && <> · <Calendar className="w-3 h-3 inline" /> scadenza {fmtData(d.expires_date)}</>}
                        </div>
                      </div>
                      <Badge className={`${meta.badge} border-0 text-[10px] flex-shrink-0`}>{meta.label}</Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 py-4">
          {data.company} · Questa pagina riporta solo lo stato di regolarità del mezzo.
          <br />Libretti, storico interventi e documenti si consultano dal gestionale aziendale.
        </p>
      </main>
    </div>
  );
}
